import { type Job, type SourceMeta } from '@cibli/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useBooth } from '@/features/settings/hooks/useBooth';
import { getJobs, streamJobs } from '@/services/job';

export function useMatchingJobs(cvId: string) {
  const { booth } = useBooth();
  const boothId = useMemo(() => booth?.id, [booth]);
  const [loading, setLoading] = useState(true);
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [sourcesMeta, setSourcesMeta] = useState<SourceMeta[]>([]);
  const [streamedJobs, setStreamedJobs] = useState<Job[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const total = useMemo(() => allJobs.length, [allJobs.length]);
  const hasSourceError = useMemo(() => sourcesMeta.some(s => s.status === 'error'), [sourcesMeta]);

  const fetchInitial = useCallback(async () => {
    try {
      setLoading(true);
      setAllJobs([]);
      setJobs([]);
      setSourcesMeta([]);
      setStreamedJobs([]);
      setIsStreaming(true);

      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;

      try {
        const stream = streamJobs(
          cvId,
          boothId,
          undefined,
          undefined,
          undefined,
          undefined,
          abortController.signal,
        );

        for await (const event of stream) {
          switch (event.type) {
            case 'source': {
              const sourceData = event.data as { source: string; jobs: Job[] };
              setStreamedJobs(prev => [...prev, ...sourceData.jobs]);
              break;
            }
            case 'done': {
              const doneData = event.data as { jobs: Job[]; sourcesMeta: SourceMeta[] };
              setAllJobs(doneData.jobs);
              setSourcesMeta(doneData.sourcesMeta);
              setJobs(doneData.jobs);
              setStreamedJobs([]);
              break;
            }
          }
        }
      } catch (streamError) {
        if (abortController.signal.aborted) return;
        console.warn('Stream failed, falling back to regular fetch:', streamError);
        const response = await getJobs(cvId, boothId);
        setAllJobs(response.jobs);
        setSourcesMeta(response.sourcesMeta);
        setJobs(response.jobs);
      }
    } catch (error) {
      console.error('Error fetching matching jobs:', error);
      setAllJobs([]);
      setJobs([]);
    } finally {
      setLoading(false);
      setIsStreaming(false);
    }
  }, [cvId, boothId]);

  useEffect(() => {
    if (!cvId) return;
    fetchInitial();

    const handleRealtimeUpdate = () => {
      fetchInitial();
    };

    window.addEventListener('cibli:jobs-updated', handleRealtimeUpdate);
    window.addEventListener('cibli:orgs-updated', handleRealtimeUpdate);

    return () => {
      abortRef.current?.abort();
      window.removeEventListener('cibli:jobs-updated', handleRealtimeUpdate);
      window.removeEventListener('cibli:orgs-updated', handleRealtimeUpdate);
    };
  }, [fetchInitial, cvId]);

  return {
    jobs,
    total,
    loading,
    loadingMore: false,
    hasMore: false,
    loadMore: () => {},
    refetch: fetchInitial,
    hasSourceError,
    sourcesMeta,
    streamedJobs,
    isStreaming,
  };
}
